// react_frontend/src/Components/Modules/Billing/Customers.js
import React, { useEffect, useState, useContext, useMemo } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";

// RSuite Components
import {
  Button,
  Input,
  IconButton,
  Tag,
  Message,
  toaster,
  Badge,
  FlexboxGrid,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaUser,
  FaPhone,
  FaDollarSign,
  FaEdit,
  FaToggleOn,
  FaToggleOff,
  FaSearch,
  FaPlus,
  FaEnvelope,
  FaFileInvoice,
} from "react-icons/fa";

// React Table
import DataTable from "react-data-table-component";

// React Router
import { Link, useNavigate } from "react-router-dom";

// SweetAlert2
import Swal from "sweetalert2";

const CustomerList = () => {
  const { customers, loadingCustomers, startCustomerPolling, stopCustomerPolling, fetchCustomers } = useContext(MotherContext);
  const [search, setSearch] = useState("");
  const [currency, setCurrency] = useState("BDT");

  const navigate = useNavigate();
  const role = localStorage.getItem("who_am_i");
  const canEdit = role === "1" || role === "2" || role === "4";

  // Start polling
  useEffect(() => {
    startCustomerPolling();
    return () => stopCustomerPolling();
  }, [startCustomerPolling, stopCustomerPolling]);

  // Fetch currency
  useEffect(() => {
    const fetchCurrency = async () => {
      try {
        const res = await AxiosAuthInstance.get("company/profile");
        setCurrency(res.data.company?.currency || "BDT");
      } catch (err) {
        console.error("Failed to fetch currency:", err);
      }
    };
    fetchCurrency();
  }, []);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "BDT",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  // ✅ Filtered data (Updated to search new fields)
  const filteredData = useMemo(() => {
    return customers
      .filter(cust => cust != null)
      .filter(cust => {
        const searchLower = search.toLowerCase();
        return (
          (cust?.customer || "").toLowerCase().includes(searchLower) ||
          (cust?.customer_phone || "").toLowerCase().includes(searchLower) ||
          (cust?.email || "").toLowerCase().includes(searchLower) ||
          (cust?.vat_number || "").toLowerCase().includes(searchLower) ||
          (cust?.cr_number || "").toLowerCase().includes(searchLower)
        );
      });
  }, [customers, search]);

  // Handle search
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Handle status toggle
  const handleStatusToggle = async (customer) => {
    const newStatus = customer.customer_status === 1 ? 0 : 1;
    const result = await Swal.fire({
      title: "Confirm Status Change",
      text: `Are you sure you want to ${newStatus === 1 ? "activate" : "deactivate"} this customer?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${newStatus === 1 ? "Activate" : "Deactivate"}`,
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.post(`update_customer_status/${customer.customer_id}/${newStatus}`);
        fetchCustomers(); // ✅ Refresh data
        toaster.push(
          <Message type="success" duration={3000}>
            Customer {newStatus === 1 ? "activated" : "deactivated"} successfully!
          </Message>,
          { placement: "topEnd" }
        );
      } catch (err) {
        toaster.push(
          <Message type="error" duration={5000}>
            Failed to update status.
          </Message>,
          { placement: "topEnd" }
        );
      }
    }
  };

  // ✅ Columns (Updated with new fields)
  const columns = [
    {
      name: "SL",
      cell: (row, index) => index + 1,
      width: "60px",
      center: true,
    },
    {
      name: "Customer",
      selector: (row) => row.customer,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaUser size={16} className="text-primary" />
          <span className="fw-medium">{row.customer}</span>
        </div>
      ),
    },
    {
      name: "Phone",
      selector: (row) => row.customer_phone,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaPhone size={14} className="text-muted" />
          <span>{row.customer_phone || "N/A"}</span>
        </div>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaEnvelope size={14} className="text-muted" />
          <span>{row.email || "N/A"}</span>
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "VAT No",
      selector: (row) => row.vat_number,
      sortable: true,
      cell: (row) => <span>{row.vat_number || "N/A"}</span>,
      minWidth: "120px",
    },
    {
      name: "CR No",
      selector: (row) => row.cr_number,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaFileInvoice size={14} className="text-muted" />
          <span>{row.cr_number || "N/A"}</span>
        </div>
      ),
      minWidth: "120px",
    },
    {
      name: "Points",
      selector: (row) => row.points,
      sortable: true,
      center: true,
      cell: (row) => (
        <Tag color="blue" style={{ fontSize: "12px" }}>
          {row.points || 0}
        </Tag>
      ),
    },
    {
      name: "Discount",
      selector: (row) => formatCurrency(row.discount),
      sortable: true,
      right: true,
    },
    {
      name: "Total Buy",
      selector: (row) => formatCurrency(row.total),
      sortable: true,
      right: true,
    },
    {
      name: "Status",
      center: true,
      cell: (row) => (
        <Badge
          content={row.customer_status === 1 ? "Active" : "Inactive"}
          color={row.customer_status === 1 ? "green" : "red"}
          style={{ fontSize: "11px", padding: "4px 8px" }}
        />
      ),
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div className="d-flex justify-content-center gap-1">
          {canEdit && (
            <IconButton
              icon={<FaEdit />}
              size="sm"
              appearance="primary"
              color="blue"
              onClick={() => navigate(`/edit_customer/${row.customer_id}`)}
              title="Edit Customer"
            />
          )}
          {canEdit && (
            <IconButton
              icon={row.customer_status === 1 ? <FaToggleOn /> : <FaToggleOff />}
              size="sm"
              appearance="primary"
              color={row.customer_status === 1 ? "green" : "orange"}
              onClick={() => handleStatusToggle(row)}
              title={row.customer_status === 1 ? "Deactivate" : "Activate"}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Breadcrumb title="Customer List" />
      <FlexboxGrid justify="space-between" align="middle" className="p-3 bg-white rounded shadow-sm mb-4">
        <FlexboxGrid.Item colspan={16}>
          <h5 className="text-dark mb-0">
            <FaUser /> Customer Management
          </h5>
          <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
            Total: <strong>{customers.length}</strong> customers
          </p>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item colspan={8}>
          <div className="d-flex gap-2">
            <Input
              placeholder="Search by name, phone, email, VAT or CR..."
              value={search}
              onChange={handleSearch}
              style={{ flex: 1 }}
              prefix={<FaSearch />}
            />
            <Button
              className="custom-bg"
              appearance="primary"
              startIcon={<FaPlus />}
              disabled={!canEdit}
              onClick={() => navigate("/add_customer")}
            >
              Add Customer
            </Button>
          </div>
        </FlexboxGrid.Item>
      </FlexboxGrid>
      <div className="p-3 bg-white rounded shadow-sm">
        <DataTable
          keyField="customer_id"
          columns={columns}
          data={filteredData}
          progressPending={loadingCustomers}
          progressComponent={<Message type="loading">Loading customers...</Message>}
          pagination
          paginationPerPage={100}
          paginationRowsPerPageOptions={[100, 250, 500, 1000]}
          highlightOnHover
          responsive
          noDataComponent={
            <div className="text-center py-5 text-muted">
              <FaUser size={48} className="mb-3 opacity-50" />
              <p>No customers found. <Link to="/add_customer">Add one</Link>.</p>
            </div>
          }
          customStyles={{
            headCells: {
              style: {
                backgroundColor: "#4351A3",
                color: "white",
                fontWeight: "600",
                fontSize: "14px",
              },
            },
            cells: {
              style: {
                padding: "12px 8px",
              },
            },
          }}
        />
      </div>
    </>
  );
};

export default CustomerList;